// Montagem do texto do PDF e escolha do parser, sem dependencia de Angular
// nem do pdf.js. O PdfParserService abre o documento e entrega os itens de
// texto; daqui para frente tudo e funcao pura - o que permite rodar a MESMA
// interpretacao fora do browser (teste com o acervo de PDFs reais) e garante
// que inclusao manual e simulador de regras leiam o documento igual.

import { NfseData, parseNfseText } from './nfse-parser';
import { isCte, parseCte } from './cte-parser';

// Um item de texto do pdf.js com a posicao que ele ocupa na pagina.
// x/y sao as coordenadas do transform (origem no canto inferior esquerdo),
// alt a altura da fonte e larg a largura medida do trecho.
export interface ItemTexto {
  str: string;
  x: number;
  y: number;
  alt: number;
  larg: number;
}

/**
 * Interpreta os itens de texto de um PDF ja aberto. Devolve undefined quando
 * nao ha texto (PDF de imagem) - quem chama decide como avisar.
 */
export function interpretarItens(paginas: ItemTexto[][], fileName: string): NfseData | undefined {
  const text = montarFluxo(paginas);

  if (!text.trim()) {
    return undefined;
  }

  // CTe (DACTE) tem layout proprio e precisa de pedagio - testa primeiro.
  // Se nao for CTe cai no parser NFSe (que tambem cobre fallback generico).
  // O texto em colunas so serve ao DANFSe v2.0, que casa rotulo e valor pela
  // coluna; montar sempre custa uma ordenacao dos itens ja extraidos, que e
  // irrelevante perto da leitura do PDF.
  const data = isCte(text)
    ? parseCte(text, fileName)
    : parseNfseText(text, fileName, montarColunas(paginas));

  data.cnpjsDocumento = cnpjsDoTexto(text);
  return data;
}

// Todos os CNPJs do texto, com ou sem mascara, que passam no digito
// verificador. Numero cru de 14 digitos so conta isolado (sem digito colado
// dos lados): chave de acesso, codigo de barras e protocolo tem trechos de 14
// digitos que nao sao CNPJ, e o DV descarta quase todos os que sobrarem.
export function cnpjsDoTexto(text: string): string[] {
  const vistos: string[] = [];
  const re = /\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}|(?<![\d./-])\d{14}(?![\d./-])/g;
  for (const m of text.matchAll(re)) {
    const d = m[0].replace(/\D/g, '');
    if (cnpjValido(d) && !vistos.includes(d)) { vistos.push(d); }
  }
  return vistos;
}

function cnpjValido(d: string): boolean {
  if (!/^\d{14}$/.test(d) || /^(\d)\1{13}$/.test(d)) { return false; }
  const dv = (base: string): number => {
    const pesos = base.length === 12
      ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
      : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma = pesos.reduce((s, p, i) => s + p * Number(base[i]), 0);
    const r = soma % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return dv(d.slice(0, 12)) === Number(d[12]) && dv(d.slice(0, 13)) === Number(d[13]);
}

// Modo FLUXO: itens na ordem em que o pdf.js entrega, quebrando a linha
// quando o y muda mais que 2. Tolerancia pequena para nao quebrar por causa
// da flutuacao de fonte dentro da mesma linha.
export function montarFluxo(paginas: ItemTexto[][]): string {
  let out = '';

  for (const itens of paginas) {
    let lastY: number | null = null;
    let line = '';
    for (const item of itens) {
      if (lastY !== null && Math.abs(item.y - lastY) > 2) {
        out += line.trimEnd() + '\n';
        line = '';
      }
      line += item.str + ' ';
      lastY = item.y;
    }
    if (line) { out += line.trimEnd() + '\n'; }
    out += '\n'; // separador entre paginas
  }

  return out;
}

// Modo COLUNAS: agrupa os itens em linhas pelo y (tolerancia proporcional a
// altura da fonte), ordena cada linha pelo x e marca a troca de coluna com
// 3+ espacos quando o buraco horizontal passa de 12 pontos.
//
// Sem isto, um documento de quatro colunas sai com os valores fora de ordem:
// o pdf.js entrega os itens na ordem do arquivo, que nao e a ordem de leitura.
export function montarColunas(paginas: ItemTexto[][]): string {
  let out = '';

  for (const itens of paginas) {
    const linhas: Array<{ y: number; itens: ItemTexto[] }> = [];

    for (const item of itens) {
      if (!item.str.trim()) { continue; }
      const tol = Math.max(2, item.alt * 0.5);
      let alvo = linhas.find(l => Math.abs(l.y - item.y) <= tol);
      if (!alvo) {
        alvo = { y: item.y, itens: [] };
        linhas.push(alvo);
      }
      alvo.itens.push(item);
    }

    linhas.sort((a, b) => b.y - a.y);

    for (const l of linhas) {
      l.itens.sort((a, b) => a.x - b.x);
      let texto = '';
      let fim: number | null = null;
      for (const item of l.itens) {
        if (fim !== null) {
          const buraco = item.x - fim;
          if (buraco > 12) { texto += '   '; }
          else if (buraco > 1) { texto += ' '; }
        }
        texto += item.str;
        fim = item.x + (item.larg || item.str.length * 4);
      }
      out += texto.trimEnd() + '\n';
    }

    out += '\n';
  }

  return out;
}
